import React, { useState, memo } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Combobox de cliente reutilizable.
 * Permite escribir para filtrar por razón social, nombre comercial, RUC/cédula o email.
 *
 * Props:
 *   value: id seleccionado
 *   onChange: (id) => void
 *   clientes: lista [{id, razon_social, nombre_comercial?, numero_identificacion?, email?, ciudad?}]
 *   placeholder: texto cuando no hay selección
 *   testId: data-testid prefix
 */
function ClienteComboboxBase({ value, onChange, clientes = [], placeholder = "Buscar cliente…", testId = "cliente-combo", className }) {
  const [open, setOpen] = useState(false);
  const selected = clientes.find(c => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("h-9 justify-between font-normal text-left w-full", !selected && "text-[#8ba397]", className)}
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate">
            {selected ? (selected.razon_social || selected.nombre_comercial) : placeholder}
          </span>
          <ChevronsUpDown size={14} className="ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[460px] p-0" align="start">
        <Command shouldFilter={true} filter={(itemValue, search) => {
          const norm = (s) => (s || "").toLowerCase();
          return norm(itemValue).includes(norm(search)) ? 1 : 0;
        }}>
          <div className="flex items-center border-b border-[#d1dbd6] px-3" cmdk-input-wrapper="">
            <Search size={14} className="mr-2 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Razón social, RUC/cédula, email…"
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-[#8ba397] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${testId}-input`}
            />
          </div>
          <CommandList className="max-h-[340px] overflow-y-auto">
            <CommandEmpty className="py-4 text-center text-sm text-[#8ba397]">Sin resultados</CommandEmpty>
            <CommandGroup>
              {clientes.map(c => {
                const searchable = [
                  c.razon_social, c.nombre_comercial, c.numero_identificacion,
                  c.email, c.ciudad,
                ].filter(Boolean).join(" ");
                return (
                  <CommandItem
                    key={c.id}
                    value={searchable}
                    onSelect={() => { onChange(c.id); setOpen(false); }}
                    data-testid={`${testId}-item-${c.id}`}
                  >
                    <Check size={14} className={cn("mr-2", value === c.id ? "opacity-100 text-[#205c45]" : "opacity-0")} />
                    <div className="flex flex-col">
                      <span className="font-medium">{c.razon_social || c.nombre_comercial}</span>
                      <span className="text-[11px] text-[#5c7368]">
                        {[c.numero_identificacion, c.ciudad, c.email].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const ClienteCombobox = memo(ClienteComboboxBase, (prev, next) => (
  prev.value === next.value &&
  prev.clientes === next.clientes &&
  prev.placeholder === next.placeholder &&
  prev.testId === next.testId &&
  prev.className === next.className
));
